import { Box } from '@material-ui/core';
import {
  Agent,
  getMerchantStructureByMerchantId,
  MerchantStructure,
  patchChangeAgentIdChannel,
} from '@nemuru-eng/js-api-client';

import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { Font } from '@/components/shared/components/Font';
import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import {
  ChangeToAnotherChannelMessageSchema,
  ChangeToAnotherChannelMessageType,
} from '@/components/widgets/AgentDetailActions/schemas/ChangeToAnotherChannelMessageSchema';
import { AgentDetailAction } from '@/components/widgets/AgentDetailActions/types/AgentDetailAction';
import MerchantStructureEntity from '@/entities/MerchantStructureEntity';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

export const ChangeToAnotherChannelMessage = ({
  agent,
  merchantStructure,
  opened,
  setOpened,
  handleClose,
}: {
  agent: Agent;
  merchantStructure: MerchantStructure;
  opened: boolean;
  setOpened: (boolean) => void;
  handleClose;
}) => {
  const { request, loading } = useRequest();
  const { revalidate } = useCache(getMerchantStructureByMerchantId, { merchantId: merchantStructure.merchant.id });

  const currentChannel = MerchantStructureEntity.create({
    merchantStructure,
    agentId: agent.id,
  }).getChannel();

  const changeToAnotherChannelDefaultValues = {
    currentChannel: currentChannel?.description || '',
    channelId: '',
  };

  const otherChannels = merchantStructure.merchant.channels
    .filter((channel) => channel.id !== currentChannel?.id)
    .map((channel) => ({
      value: channel.id,
      display: channel.description,
    }));

  const onSubmit = async (useFormValues: ChangeToAnotherChannelMessageType): Promise<void> => {
    if (!currentChannel) return;
    await request(patchChangeAgentIdChannel, {
      agentId: agent.id,
      currentChannelId: currentChannel.id,
      newChannelId: useFormValues.channelId,
    });
    handleClose();
    revalidate();
  };

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={handleClose}
      title={AgentDetailAction.CHANGE_TO_ANOTHER_CHANNEL}
      saveButtonTextId={`${AgentDetailAction.CHANGE_TO_ANOTHER_CHANNEL}_BTN`}
      onSubmitForm={onSubmit}
      isLoading={loading}
      formSchema={ChangeToAnotherChannelMessageSchema}
      defaultValues={changeToAnotherChannelDefaultValues}
    >
      <>
        <Font variant={'subtitle2'} color={'textSecondary'}>
          <Text tId={`${AgentDetailAction.CHANGE_TO_ANOTHER_CHANNEL}_TEXT`} />
        </Font>
        <Box mt={3} />
        <FormGridContainer fieldVariant={FieldVariant.form} fullWidth>
          <TextFormField label={'CURRENT_CHANNEL'} name={'currentChannel'} value={currentChannel?.description} />
          <SelectFormField label={'NEW_CHANNEL'} name={'channelId'} selectOptions={otherChannels} />
        </FormGridContainer>
      </>
    </EditDialogWrapper>
  );
};
